import {use, useMemo} from 'react'

import {Button, Text} from '@primer/react-brand/esm'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {
  PLAN_TYPES,
  PlanTypeSegmentedControl,
} from '../../../../../brand/components/PlanTypeSegmentedControl/PlanTypeSegmentedControl'
import {Section} from '../../../../../brand/components/BrandPage'
import type {GenericGroup, GenericContent, GenericSectionWithIds} from '../../../../../brand/lib/types/contentful'

import {PlanTypeContext} from '../../_context/PlanTypeContext'

import {PricingPlans} from '../../_components/PricingPlans/PricingPlans'
import IdeList from '../../_components/IdeList/IdeList'

import styles from './PricingSection.module.css'

type Props = {
  contentfulContent: GenericSectionWithIds
}

export function PricingSection(props: Props) {
  const {contentfulContent} = props

  const {sectionIntro} = contentfulContent.fields

  const {featuresCopilotPlanTypes, featuresCopilotPricingOptions, featuresCopilotPricingSectionIdeList} =
    contentfulContent.ids as {
      featuresCopilotPlanTypes: GenericContent
      featuresCopilotPricingOptions: GenericGroup
      featuresCopilotPricingSectionIdeList: GenericContent
    }

  const ideListCtaLink = featuresCopilotPricingSectionIdeList.fields.links?.at(0)

  const {planType, setPlanType} = use(PlanTypeContext)

  const planItems = useMemo(
    () =>
      featuresCopilotPlanTypes?.fields.content?.map(item => ({
        id: `${item.fields.id}-plan-tab`,
        value: item.fields.id,
        label: item.fields.heading,
      })) || [
        {
          id: 'individual-plan-tab',
          value: PLAN_TYPES.Individual,
          label: 'For individuals',
        },
        {
          id: 'business-plan-tab',
          value: PLAN_TYPES.Business,
          label: 'For businesses',
        },
      ],
    [featuresCopilotPlanTypes?.fields.content],
  )

  return (
    <Section id="pricing" introComponent={sectionIntro} introClassName={styles.sectionIntro} withBorder>
      {featuresCopilotPlanTypes ? (
        <div className="d-flex flex-justify-center px-2 tmp-mb-7">
          <PlanTypeSegmentedControl
            value={planType}
            onChange={setPlanType}
            heading={featuresCopilotPlanTypes.fields.heading!}
            items={planItems}
          />
        </div>
      ) : null}

      {featuresCopilotPricingOptions ? <PricingPlans contentfulContent={featuresCopilotPricingOptions} /> : null}

      {featuresCopilotPricingSectionIdeList ? (
        <div>
          <Text as="p" size="200" variant="muted" weight="normal" align="center" className="tmp-mb-5">
            {featuresCopilotPricingSectionIdeList.fields.heading}
          </Text>

          <IdeList type="small" location="pricing-cards" />

          {ideListCtaLink ? (
            <div className="d-flex flex-justify-center tmp-mt-7">
              <Button
                as="a"
                href={ideListCtaLink.fields.href}
                size="medium"
                variant={ideListCtaLink.fields.variant || 'subtle'}
                {...getAnalyticsEvent({
                  action: ideListCtaLink.fields.text,
                  tag: 'button',
                  context: 'ide_list',
                  location: 'cta',
                })}
              >
                {ideListCtaLink.fields.text}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Section>
  )
}

export default PricingSection
